import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Training } from '@/types/trainings';
import { format, startOfWeek, endOfWeek, eachDayOfInterval, subDays, addDays } from 'date-fns';

interface UseInstructorDailyTotalsProps {
  instructorId: string;
  selectedDate: Date;
}

export const useInstructorDailyTotals = ({ instructorId, selectedDate }: UseInstructorDailyTotalsProps) => {

  const { data: dailyTotals, isLoading, error } = useQuery({
    queryKey: ['instructor-daily-totals', instructorId, format(selectedDate, 'yyyy-MM-dd')],
    queryFn: async () => {
      // Generate the same range as DailySlider (3 weeks: previous, current, next)
      const currentDate = new Date();
      
      // Add previous week
      const prevWeekStart = startOfWeek(subDays(currentDate, 7), { weekStartsOn: 1 });
      const prevWeekEnd = endOfWeek(subDays(currentDate, 7), { weekStartsOn: 1 });
      
      // Add current week
      const currentWeekStart = startOfWeek(currentDate, { weekStartsOn: 1 });
      const currentWeekEnd = endOfWeek(currentDate, { weekStartsOn: 1 });
      
      // Add next week
      const nextWeekStart = startOfWeek(addDays(currentDate, 7), { weekStartsOn: 1 });
      const nextWeekEnd = endOfWeek(addDays(currentDate, 7), { weekStartsOn: 1 });
      
      // Get the overall range
      const overallStart = prevWeekStart;
      const overallEnd = nextWeekEnd;
      
      // Get all classes for the instructor in the entire range
      const { data: classes, error: classesError } = await supabase
        .from('trainings')
        .select('start_time')
        .eq('user_id', instructorId)
        .eq('is_class', true)
        .gte('start_time', overallStart.toISOString())
        .lte('start_time', overallEnd.toISOString())
        .order('start_time', { ascending: true });

      if (classesError) {
        console.error('Error fetching instructor classes for daily totals:', classesError);
        throw classesError;
      }

      // Generate all days in the same range as DailySlider
      const days = [];
      
      // Add previous week
      const prevWeekDays = eachDayOfInterval({ start: prevWeekStart, end: prevWeekEnd });
      days.push(...prevWeekDays);
      
      // Add current week
      const currentWeekDays = eachDayOfInterval({ start: currentWeekStart, end: currentWeekEnd });
      days.push(...currentWeekDays);
      
      // Add next week
      const nextWeekDays = eachDayOfInterval({ start: nextWeekStart, end: nextWeekEnd });
      days.push(...nextWeekDays);
      
      const weekDays = days;
      
      // Calculate class count for each day
      const totals = weekDays.map(day => {
        const dayStart = new Date(day);
        dayStart.setHours(0, 0, 0, 0);
        const dayEnd = new Date(day);
        dayEnd.setHours(23, 59, 59, 999);

        const classesForDay = classes?.filter(cls => {
          const classDate = new Date(cls.start_time);
          return classDate >= dayStart && classDate <= dayEnd;
        }) || [];

        const classCount = classesForDay.length;

        return {
          date: format(day, 'yyyy-MM-dd'),
          class_count: classCount
        };
      });

      return totals;
    },
    enabled: !!instructorId,
  });

  return {
    dailyTotals: dailyTotals || [],
    isLoading,
    error,
  };
};
